import { notifications } from "@mantine/notifications";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useAuthStore } from "../../auth/store";
import { SseParser } from "./sseParser";
import { classifyStreamOutcome } from "./streamOutcome";
import { useNotificationStreamStore } from "./streamStatus";
import type { NotificationStreamStatus } from "./streamStatus";
import type { NotificationDTO } from "./types";

// React-hook: подписка на SSE-поток live-нотификаций.
//
// Контракт:
// - Endpoint `GET /api/v1/notifications/stream`, аутентификация —
//   HttpOnly cookie `baton_session` (#916): токена в JS нет, его
//   нельзя положить ни в URL, ни в заголовок. Запрос идёт с
//   `credentials: "include"`, браузер сам прикладывает cookie
//   same-origin.
// - `EventSource` не используем (хотя cookie он бы и слал) — нам
//   нужен ручной reconnect/back-off и парсинг, поэтому fetch() с
//   `signal` от `AbortController`, тело через `getReader()` +
//   UTF-8 decoder + `SseParser`.
// - Heartbeat (`: ping\n\n`) — отбрасывается на уровне парсера.
// - На каждый `data: <json>` событие — показываем toast через
//   `notifications.show(...)` (Mantine).
// - Reconnect: exponential back-off 1s → 2s → 4s → 8s → cap 30s.
//   Сбрасывается в 1s после успешного `fetch` (response.ok), так что
//   разовая «икра» сети не накапливает задержку.
// - Классификация ответа — через `classifyStreamOutcome` (правило по
//   семейству статуса живёт там). `authFailed` (401/403) — токен
//   невалиден, реконнект бесполезен, молча выходим: редирект на
//   логин сделает следующий `authedFetch`. `permanent` (прочие 4xx,
//   напр. 404) — сервер жив, но так это не заработает никогда;
//   выходим из цикла навсегда и публикуем статус `unavailable`.
//   `retryable` (5xx, сетевой сбой, `408`/`429`, успешный ответ) —
//   ретраим с back-off и публикуем `reconnecting`/`live`.
// - `reconnecting` публикуется, только начиная со второй подряд
//   неудачной попытки (`RECONNECTING_AFTER_CONSECUTIVE_FAILURES`).
//   Причина: успешный `response`, оборвавшийся сразу же (EOF/сетевая
//   ошибка мгновенно после коннекта), иначе переключал бы индикатор
//   `live` ↔ `reconnecting` с периодом в 1 секунду при каждом цикле
//   «конект-обрыв-реконнект» — заметное мигание assertive-региона
//   (Mantine `Alert role="alert"`, скринридер объявляет на каждый
//   показ). Первая неудача проходит тихо: если соединение
//   восстановится сразу, пользователь ничего не увидит; не
//   восстановится — индикатор появится со второй попытки. Счётчик
//   подряд идущих неудач сбрасывается НЕ на факте установленного
//   `response` (это сделал бы back-off — см. ниже), а на первом
//   реально полученном SSE-событии из потока (первый непустой
//   `payload` из `SseParser.feed`). Соединение, которое открылось и
//   тут же закрылось, не доставив ни одного event'а, неудачей быть не
//   перестаёт: иначе цикл «коннект → мгновенный EOF → back-off →
//   коннект → …» обнулял бы счётчик на каждом витке, и порог
//   никогда бы не достигался — статус навсегда оставался бы `live`,
//   хотя ни одного события так и не было доставлено (ревью PR#2070,
//   2-я итерация).
// - Статус стрима — глобальное состояние с одним владельцем
//   (`useNotificationStreamStore`). Опция `trackStatus` (default
//   `false`) решает, пишет ли конкретная подписка в этот стор: только
//   один подписчик должен быть владельцем (`NotificationStreamSubscriber`
//   в `ProtectedRoute`, передаёт `trackStatus: true` явно). Default
//   `false`, а не `true` — новая подписка, забывшая передать опцию,
//   не должна тихо становиться совладельцем индикатора: на unmount
//   она затёрла бы `unavailable`/`reconnecting` глобального стрима
//   значением `idle`, и оператор перестал бы видеть, что стрим
//   мёртв, хотя он остался бы мёртвым (терминальная подписка сама
//   больше ничего не опубликует).
//
// Cleanup: `useEffect` return → `controller.abort()`. fetch-promise
// resolve'нется с DOMException 'AbortError', цикл выйдет.

const URL_NOTIFICATIONS_STREAM = "/api/v1/notifications/stream";
// Back-off table — степени двойки до 30 секунд. После 30s остаёмся
// на 30s бесконечно: нагрузить backend retry-штормом нежелательно,
// при долгом outage достаточно одного reconnect-attempt'а / 30s.
const RECONNECT_INITIAL_MS = 1000;
const RECONNECT_MAX_MS = 30000;
// Порог, с которого неудачная попытка становится видимой оператору.
const RECONNECTING_AFTER_CONSECUTIVE_FAILURES = 2;

/**
 * Подписаться на SSE-стрим нотификаций пока компонент смонтирован.
 *
 * Эффект перезапускается на смену identity (relogin → новая org →
 * новая queue в backend broadcaster'е). Если identity отсутствует,
 * эффект не стартует (страница без auth не должна открывать стрим).
 *
 * Hook возвращает `void`: API наружу — побочный эффект (toast'ы).
 * Внутреннее состояние reconnect-loop не нужно потребителю.
 *
 * `onMessage` — опциональный callback для query-invalidation на
 * странице задачи. Toast'ы шлются всегда; callback вызывается на
 * каждое валидное DTO до toast'а.
 */
export interface UseNotificationStreamOptions {
  readonly onMessage?: (dto: NotificationDTO) => void;
  readonly enabled?: boolean;
  /**
   * Показывать toast'ы при получении событий. Default true.
   *
   * Если страница уже находится внутри `<ProtectedRoute>` —
   * там монтируется глобальный `NotificationStreamSubscriber`,
   * который сам показывает toast'ы. Page-level подписки (например
   * TaskDetailPage для invalidate-логики) должны передавать
   * `showToast: false`, иначе у пользователя удвоятся уведомления.
   */
  readonly showToast?: boolean;
  /**
   * Публиковать статус стрима в глобальный `useNotificationStreamStore`.
   * Default `false` — fail-safe: владелец индикатора должен объявить
   * это явно, а не унаследовать по умолчанию.
   *
   * Статус — глобальное состояние с одним владельцем: подписчик из
   * `ProtectedRoute` (`NotificationStreamSubscriber`), он передаёт
   * `trackStatus: true` явно. Page-level подписки (например
   * `TaskDetailPage` для invalidate-логики) опцию не передают вовсе —
   * иначе, будь дефолт `true`, их размонтирование при уходе со
   * страницы стёрло бы индикатор, принадлежащий глобальному стриму
   * (и, если стрим к тому моменту терминально мёртв, стёрло бы это
   * молча: подписка-невладелец больше ничего не опубликует).
   */
  readonly trackStatus?: boolean;
}

export function useNotificationStream(
  options?: UseNotificationStreamOptions,
): void {
  const identity = useAuthStore((state) => state.identity);
  const { t } = useTranslation();
  const onMessage = options?.onMessage;
  const enabled = options?.enabled ?? true;
  const showToast = options?.showToast ?? true;
  const trackStatus = options?.trackStatus ?? false;
  const orgId = identity?.orgId ?? null;

  useEffect(() => {
    if (orgId === null || !enabled) {
      return;
    }

    const controller = new AbortController();
    let cancelled = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let backoffMs = RECONNECT_INITIAL_MS;
    let consecutiveFailures = 0;

    function publishStatus(status: NotificationStreamStatus): void {
      if (!trackStatus || cancelled) {
        return;
      }
      useNotificationStreamStore.getState().setStatus(status);
    }

    /**
     * Неудачная попытка (сетевая ошибка, `retryable`-статус, EOF/обрыв
     * ридера) — учитываем в счётчике и публикуем `reconnecting` только
     * начиная со второй подряд. Первая неудача остаётся тихой, чтобы
     * мгновенное восстановление не мигало индикатором.
     */
    function noteRetryableFailure(): void {
      consecutiveFailures += 1;
      if (consecutiveFailures >= RECONNECTING_AFTER_CONSECUTIVE_FAILURES) {
        publishStatus("reconnecting");
      }
    }

    function showToastForDto(dto: NotificationDTO): void {
      // discriminator-switch с exhaustiveness — новый type в DTO
      // подсветится TS-ошибкой здесь же.
      switch (dto.type) {
        case "task_status_changed":
          notifications.show({
            title: t("notifications.taskStatusChanged.title"),
            message: t("notifications.taskStatusChanged.message", {
              taskId: dto.task_id,
              oldStatus: dto.old_status ?? "—",
              newStatus: dto.new_status,
            }),
            color: "blue",
          });
          return;
        case "agent_progress":
          notifications.show({
            title: t("notifications.agentProgress.title"),
            message: t("notifications.agentProgress.message", {
              agentRole: dto.agent_role,
              text: dto.text,
            }),
            color: "teal",
          });
          return;
      }
    }

    async function runStream(): Promise<void> {
      // Внешний цикл — переподключения. Внутри одна итерация =
      // одно успешное (или сразу упавшее) соединение.
      while (!cancelled) {
        let response: Response;
        try {
          response = await fetch(URL_NOTIFICATIONS_STREAM, {
            method: "GET",
            headers: {
              Accept: "text/event-stream",
            },
            // `credentials: "include"` — браузер прикладывает HttpOnly
            // cookie `baton_session`; токена в JS нет (#916).
            credentials: "include",
            signal: controller.signal,
            // `cache: no-store` — на всякий случай: некоторые прокси
            // могут кэшировать SSE response, если SW зарегистрирован.
            cache: "no-store",
          });
        } catch {
          // fetch fail = сеть упала, abort, или TypeError'ом
          // браузер закрыл connection. AbortError → cancelled=true,
          // выйдем сразу. Иначе — back-off и retry.
          if (cancelled) {
            return;
          }
          noteRetryableFailure();
          await scheduleReconnect();
          continue;
        }

        const outcome = classifyStreamOutcome(response.status);
        if (outcome === "authFailed") {
          // Токен невалиден — реконнект бесполезен. Никаких toast'ов
          // об ошибке: 401/403 — это «выкинули из системы», UI получит
          // редирект на login через ProtectedRoute, когда сделает
          // следующий authedFetch.
          publishStatus("idle");
          return;
        }
        if (outcome === "permanent") {
          // Сервер жив и отвечает, что запрос в таком виде не
          // выполнится (напр. 404) — повтор ничего не изменит до
          // передеплоя. Выходим из цикла навсегда и сообщаем об этом.
          publishStatus("unavailable");
          return;
        }
        if (!response.ok || response.body === null) {
          // 5xx / отсутствие body → reconnect c back-off.
          noteRetryableFailure();
          await scheduleReconnect();
          continue;
        }

        // Успешный response — сбрасываем back-off; даже если стрим
        // через 30 минут оборвётся, следующее переподключение
        // стартует с 1s, а не с 30s. Счётчик подряд идущих неудач НЕ
        // сбрасываем здесь — сам факт открытого соединения ещё не
        // значит, что стрим реально работает (см. комментарий выше
        // хука): сброс — ниже, на первом полученном payload'е.
        backoffMs = RECONNECT_INITIAL_MS;
        publishStatus("live");

        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        const parser = new SseParser();
        try {
          while (!cancelled) {
            const { value, done } = await reader.read();
            if (done) {
              break;
            }
            const chunk = decoder.decode(value, { stream: true });
            for (const payload of parser.feed(chunk)) {
              // Стрим реально заработал — доставил хотя бы один SSE
              // data-frame (heartbeat'и сюда не попадают, их
              // отфильтровывает `SseParser`). Сбрасываем счётчик
              // подряд идущих неудач именно здесь, а не на факте
              // коннекта: соединение, открывшееся и тут же
              // закрывшееся без единого event'а, неудачей быть не
              // перестаёт.
              consecutiveFailures = 0;
              let dto: NotificationDTO;
              try {
                dto = JSON.parse(payload) as NotificationDTO;
              } catch {
                // Битый JSON — пропускаем event, но не рвём стрим.
                continue;
              }
              if (onMessage !== undefined) {
                onMessage(dto);
              }
              if (showToast) {
                showToastForDto(dto);
              }
            }
          }
        } catch {
          // Чтение упало (сеть, abort) — выйдем из inner-loop, и
          // если не cancelled, попробуем переподключиться.
          if (cancelled) {
            return;
          }
        }
        if (cancelled) {
          return;
        }
        // EOF без cancel — backend закрыл стрим. Reconnect.
        noteRetryableFailure();
        await scheduleReconnect();
      }
    }

    function scheduleReconnect(): Promise<void> {
      // Промис, который резолвится через `backoffMs`, потом удваивает
      // задержку до cap'а. Возвращаем promise, чтобы caller мог
      // `await` и продолжить main loop'ом.
      return new Promise((resolve) => {
        reconnectTimer = setTimeout(() => {
          reconnectTimer = null;
          backoffMs = Math.min(backoffMs * 2, RECONNECT_MAX_MS);
          resolve();
        }, backoffMs);
      });
    }

    void runStream();

    return () => {
      cancelled = true;
      controller.abort();
      if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (trackStatus) {
        useNotificationStreamStore.getState().setStatus("idle");
      }
    };
  }, [orgId, t, onMessage, enabled, showToast, trackStatus]);
}
